import { CartUpdateEvent } from '@theme/events';

/**
 * HHC.FR — contenu de secours pour la popup Quick Add.
 *
 * Le Quick Add natif Horizon récupère normalement son contenu en allant
 * chercher la vraie fiche produit (section product-information) et en y
 * cherchant un repère [data-product-grid-content]. Tant que cette fiche
 * produit native n'est pas activée (page produit encore gérée par
 * GemPages), ce repère n'existe pas et la popup native reste vide.
 *
 * Ce script comble ce vide avec les informations déjà présentes sur la
 * vignette (titre, format, prix, phrase marketing), un "Ajouter au panier"
 * fonctionnel via l'API panier standard de Shopify (/cart/add.js), et,
 * pour les produits à plusieurs variantes, le vrai sélecteur de variantes
 * natif Horizon (snippets/variant-quick-add.liquid, rendu par la section
 * native sections/section-rendering-product-card.liquid via l'API Section
 * Rendering — le même mécanisme que celui déjà utilisé par
 * assets/variant-picker.js pour ses propres mises à jour). Aucune logique
 * de sélection de variante n'est réimplémentée ici : une fois le picker
 * natif inséré dans le DOM, assets/variant-picker.js (déjà chargé
 * globalement, non modifié) prend le relais pour la sélection, la mise à
 * jour du prix (assets/product-price.js) et la mise à jour de l'input
 * variant-id (assets/product-form.js), par délégation d'événements sur
 * l'ancêtre <dialog> commun — comportement natif Horizon existant.
 *
 * Mission compactage : le prix au gramme affiché ici n'est jamais calculé
 * en JS à partir d'un texte formaté — il est précalculé côté serveur
 * (snippets/variant-quick-add.liquid, data-price-per-gram par variante,
 * données natives Shopify). Ce script se contente de lire cet attribut sur
 * l'input actuellement coché : aucun parsing de prix, aucune requête
 * réseau supplémentaire, aucun polling/interval.
 *
 * Dès que la fiche produit native sera activée, ce script n'aura plus
 * d'effet : il ne s'exécute que si aucun contenu natif n'a déjà été injecté
 * (voir la garde dans handleQuickAddClick).
 */

function buildMedia(data) {
  if (!data.image) return null;
  const media = document.createElement('div');
  media.className = 'hhc-quick-add-fallback__media';
  const img = document.createElement('img');
  img.src = data.image;
  img.alt = data.imageAlt || data.title;
  img.width = 400;
  img.height = 400;
  img.loading = 'eager';
  media.appendChild(img);
  return media;
}

/**
 * En-tête commun aux deux variantes de popup (produit à variante unique et multi-variantes) :
 * média compact, titre, extrait de description sur 2 lignes (CSS line-clamp) + lien "Voir la
 * fiche produit complète" natif vers la fiche produit réelle (aucun JavaScript de navigation,
 * aucune seconde modale) — rendu volontairement bien visible (retour utilisateur direct).
 */
function buildHeader(data, wrapper) {
  const media = buildMedia(data);
  if (media) wrapper.appendChild(media);

  const title = document.createElement('h2');
  title.className = 'hhc-quick-add-fallback__title';
  title.textContent = data.title;
  wrapper.appendChild(title);

  if (data.marketing) {
    const marketing = document.createElement('p');
    marketing.className = 'hhc-quick-add-fallback__marketing';
    marketing.textContent = data.marketing;
    wrapper.appendChild(marketing);

    const readMore = document.createElement('a');
    readMore.href = data.productUrl;
    readMore.className = 'hhc-quick-add-fallback__read-more';
    readMore.textContent = 'Voir la fiche produit complète →';
    wrapper.appendChild(readMore);
  }
}

/**
 * Stepper de quantité (− valeur +), remplace l'ancien champ numérique isolé.
 * Retourne un getter simple ; la valeur réelle est stockée sur le conteneur (dataset), lue à
 * chaque clic sur "Ajouter au panier" pour garantir que la quantité envoyée au panier est
 * toujours exactement celle affichée à l'écran.
 */
function buildQuantityStepper() {
  const stepper = document.createElement('div');
  stepper.className = 'hhc-quick-add-fallback__qty-stepper';
  stepper.setAttribute('role', 'group');
  stepper.setAttribute('aria-label', 'Quantité');
  stepper.dataset.quantity = '1';

  const minusBtn = document.createElement('button');
  minusBtn.type = 'button';
  minusBtn.className = 'hhc-quick-add-fallback__qty-btn hhc-quick-add-fallback__qty-btn--minus';
  minusBtn.setAttribute('aria-label', 'Diminuer la quantité');
  minusBtn.textContent = '−';

  const valueEl = document.createElement('span');
  valueEl.className = 'hhc-quick-add-fallback__qty-value';
  valueEl.setAttribute('aria-live', 'polite');
  valueEl.textContent = '1';

  const plusBtn = document.createElement('button');
  plusBtn.type = 'button';
  plusBtn.className = 'hhc-quick-add-fallback__qty-btn hhc-quick-add-fallback__qty-btn--plus';
  plusBtn.setAttribute('aria-label', 'Augmenter la quantité');
  plusBtn.textContent = '+';

  function refresh() {
    const qty = parseInt(stepper.dataset.quantity, 10) || 1;
    valueEl.textContent = String(qty);
    minusBtn.disabled = qty <= 1;
  }

  minusBtn.addEventListener('click', () => {
    const qty = parseInt(stepper.dataset.quantity, 10) || 1;
    if (qty > 1) {
      stepper.dataset.quantity = String(qty - 1);
      refresh();
    }
  });

  plusBtn.addEventListener('click', () => {
    const qty = parseInt(stepper.dataset.quantity, 10) || 1;
    stepper.dataset.quantity = String(qty + 1);
    refresh();
  });

  refresh();
  stepper.append(minusBtn, valueEl, plusBtn);
  return stepper;
}

async function submitAddToCart({ variantId, stepper, status, addButton }) {
  addButton.disabled = true;
  status.textContent = 'Ajout en cours…';
  try {
    const quantity = Math.max(1, parseInt(stepper.dataset.quantity, 10) || 1);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.description || result.message || 'Erreur');
    }
    status.textContent = 'Ajouté au panier ✓';
    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    document.dispatchEvent(
      new CartUpdateEvent(null, 'hhc-quick-add-fallback', { itemCount: cart.item_count })
    );
    const dialog = document.getElementById('quick-add-dialog');
    setTimeout(() => {
      dialog?.closeDialog?.();
    }, 1200);
  } catch (error) {
    status.textContent = "Impossible d'ajouter ce produit pour le moment.";
    addButton.disabled = false;
  }
}

/**
 * Contenu pour un produit à variante unique : pas de picker (inutile), ligne compacte
 * prix sélectionné + quantité, bouton d'ajout.
 */
function buildFallbackContent(data) {
  const wrapper = document.createElement('div');
  wrapper.className = 'hhc-quick-add-fallback';
  buildHeader(data, wrapper);

  if (!data.available) {
    const unavailable = document.createElement('p');
    unavailable.className = 'hhc-quick-add-fallback__status';
    unavailable.textContent = 'Épuisé';
    wrapper.appendChild(unavailable);
    return wrapper;
  }

  const row = document.createElement('div');
  row.className = 'hhc-quick-add-fallback__row';

  const priceInfo = document.createElement('div');
  priceInfo.className = 'hhc-quick-add-fallback__price-info';
  // HHC.FR : pas de picker natif ici (variante unique), donc pas de data-price-per-gram
  // disponible sans re-parser un prix déjà formaté — on affiche le prix total courant,
  // déjà exact et fourni par Liquid (aucune invention de valeur).
  priceInfo.textContent = `Prix sélectionné : ${data.price}`;
  row.appendChild(priceInfo);

  const stepper = buildQuantityStepper();
  row.appendChild(stepper);
  wrapper.appendChild(row);

  const status = document.createElement('p');
  status.className = 'hhc-quick-add-fallback__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  wrapper.appendChild(status);

  const addButton = document.createElement('button');
  addButton.type = 'button';
  addButton.className = 'button hhc-quick-add-fallback__add';
  addButton.textContent = 'Ajouter au panier';
  wrapper.appendChild(addButton);

  addButton.addEventListener('click', () => {
    submitAddToCart({ variantId: data.variantId, stepper, status, addButton });
  });

  return wrapper;
}

/**
 * Contenu pour les produits à plusieurs variantes : réutilise le vrai picker natif Horizon
 * (variant-picker + product-price), obtenu via l'API Section Rendering native de Shopify —
 * aucune donnée de variante n'est construite ou devinée côté JS.
 */
async function buildMultiVariantContent(data) {
  const wrapper = document.createElement('div');
  wrapper.className = 'hhc-quick-add-fallback';
  buildHeader(data, wrapper);

  const loading = document.createElement('p');
  loading.className = 'hhc-quick-add-fallback__status';
  loading.textContent = 'Chargement des formats…';
  wrapper.appendChild(loading);

  let doc;
  try {
    const response = await fetch(
      `${data.productUrl}?section_id=section-rendering-product-card&option_values=`,
      { credentials: 'same-origin' }
    );
    const html = await response.text();
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch (error) {
    loading.textContent = "Impossible de charger les formats pour le moment.";
    return wrapper;
  }

  const variantPicker = doc.querySelector('variant-picker');
  const productPrice = doc.querySelector('product-price');
  if (!variantPicker || !productPrice) {
    loading.textContent = "Impossible de charger les formats pour le moment.";
    return wrapper;
  }
  loading.remove();

  // HHC.FR — assets/product-price.js n'applique la mise à jour de prix que si son propre
  // data-product-id correspond à celui de l'élément qui déclenche l'événement (le variant-picker) ;
  // la section native ne pose pas cet attribut sur <product-price>, ce qui faisait échouer
  // silencieusement la comparaison et bloquait la mise à jour visuelle du prix (l'ajout au panier
  // restait pourtant correct, car lu indépendamment sur le picker au moment du clic).
  productPrice.dataset.productId = variantPicker.dataset.productId;

  // HHC.FR — assets/variant-picker.js ne route ses mises à jour vers la section native
  // section-rendering-product-card (au lieu de tenter de recharger la fiche produit complète,
  // toujours désactivée) que si le <variant-picker> a un ancêtre <quick-add-component> — c'est
  // exactement ainsi que ce composant natif Horizon est conçu pour être utilisé en contexte
  // Quick Add (cf. assets/variant-picker.js, méthode buildRequestUrl). La popup partagée
  // (snippets/quick-add-modal.liquid) n'en contient pas par défaut (ce wrapper appartient
  // normalement à chaque carte, pas à la modale) : on l'ajoute donc ici pour que le mécanisme
  // natif déjà chargé (aucun nouveau JS de sélection de variante) se déclenche correctement.
  const nativeScope = document.createElement('quick-add-component');
  nativeScope.appendChild(variantPicker);
  nativeScope.appendChild(productPrice);
  wrapper.appendChild(nativeScope);

  // HHC.FR — Mission compactage : ligne unique prix au gramme (ou prix sélectionné à défaut) +
  // quantité, remplace l'ancien bloc de prix isolé + champ numérique isolé. <product-price> reste
  // dans le DOM (jamais supprimé, jamais display:none sur son propre contenu de mise à jour) : il
  // est simplement déplacé visuellement dans cette ligne, et masqué UNIQUEMENT quand le prix au
  // gramme (information non redondante) est disponible pour ne pas afficher deux prix à la fois.
  // display:none sur <product-price> lui-même n'empêche ni ses écouteurs d'événements ni la
  // logique native de product-price.js de continuer à mettre à jour son contenu interne — seul le
  // rendu visuel est concerné.
  const row = document.createElement('div');
  row.className = 'hhc-quick-add-fallback__row';

  const priceInfo = document.createElement('div');
  priceInfo.className = 'hhc-quick-add-fallback__price-info';
  const gramPriceEl = document.createElement('span');
  gramPriceEl.className = 'hhc-quick-add-fallback__gram-price';
  priceInfo.appendChild(gramPriceEl);
  priceInfo.appendChild(productPrice);
  row.appendChild(priceInfo);

  function refreshPriceInfo() {
    const checked = variantPicker.querySelector('input[type="radio"]:checked');
    const gramPrice = checked?.dataset.pricePerGram || '';
    if (gramPrice) {
      gramPriceEl.textContent = `Soit ${gramPrice}`;
      gramPriceEl.hidden = false;
      productPrice.style.display = 'none';
    } else {
      gramPriceEl.hidden = true;
      productPrice.style.display = '';
    }
  }
  refreshPriceInfo();
  // Lecture seule d'un attribut déjà calculé côté serveur à chaque changement de variante —
  // aucune logique de sélection réimplémentée, aucun recalcul, aucune requête réseau ici.
  nativeScope.addEventListener('change', refreshPriceInfo);

  const stepper = buildQuantityStepper();
  row.appendChild(stepper);
  wrapper.appendChild(row);

  const status = document.createElement('p');
  status.className = 'hhc-quick-add-fallback__status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  wrapper.appendChild(status);

  const addButton = document.createElement('button');
  addButton.type = 'button';
  addButton.className = 'button hhc-quick-add-fallback__add';
  addButton.textContent = 'Ajouter au panier';
  wrapper.appendChild(addButton);

  const checkedInput = variantPicker.querySelector('input[type="radio"]:checked');
  if (!checkedInput || checkedInput.dataset.optionAvailable === 'false') {
    addButton.disabled = true;
    status.textContent = 'Épuisé';
  }

  addButton.addEventListener('click', () => {
    const selected = variantPicker.querySelector('input[type="radio"]:checked');
    if (!selected || !selected.dataset.variantId || selected.dataset.optionAvailable === 'false') {
      status.textContent = 'Veuillez choisir un format disponible.';
      return;
    }
    submitAddToCart({ variantId: selected.dataset.variantId, stepper, status, addButton });
  });

  return wrapper;
}

function handleQuickAddClick(event) {
  const button = event.target.closest('.quick-add__button');
  if (!button || button.disabled) return;
  const host = button.closest('quick-add-component');
  if (!host) return;
  const modalContent = document.getElementById('quick-add-modal-content');
  if (!modalContent) return;
  // Ne pas écraser un contenu déjà chargé depuis la vraie fiche produit
  // native (si elle est un jour activée).
  if (modalContent.querySelector('[data-product-grid-content]')) return;

  const data = {
    title: host.dataset.fallbackTitle || '',
    format: host.dataset.fallbackFormat || '',
    price: host.dataset.fallbackPrice || '',
    comparePrice: host.dataset.fallbackComparePrice || '',
    gramSuffix: host.dataset.fallbackGramSuffix || '',
    marketing: host.dataset.fallbackMarketing || '',
    variantId: host.dataset.fallbackVariantId || '',
    available: host.dataset.fallbackAvailable === 'true',
    productUrl: host.dataset.fallbackProductUrl || '#',
    image: host.dataset.fallbackImage || '',
    imageAlt: host.dataset.fallbackImageAlt || '',
    variantsCount: parseInt(host.dataset.fallbackVariantsCount, 10) || 1,
  };

  if (data.variantsCount > 1) {
    // Jeton de course : si un autre Quick Add est ouvert avant la fin du fetch, on abandonne
    // silencieusement l'injection du contenu obsolète plutôt que d'afficher le mauvais produit.
    const token = Symbol('quick-add-request');
    modalContent.dataset.hhcRequestToken = '';
    modalContent._hhcRequestToken = token;
    modalContent.replaceChildren();
    const loadingPlaceholder = document.createElement('p');
    loadingPlaceholder.className = 'hhc-quick-add-fallback__status';
    loadingPlaceholder.textContent = 'Chargement…';
    modalContent.appendChild(loadingPlaceholder);

    buildMultiVariantContent(data).then((content) => {
      if (modalContent._hhcRequestToken !== token) return;
      modalContent.replaceChildren(content);
    });
  } else {
    modalContent.replaceChildren(buildFallbackContent(data));
  }
}

document.addEventListener('click', handleQuickAddClick, true);
